import { lazy, ComponentType } from 'react';

/**
 * A wrapper around React.lazy that attempts to retry the import if it fails.
 * This is particularly useful for handling "Failed to fetch dynamically imported module" errors
 * which occur when a new version of the app is deployed and old chunks are removed.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T } | T>,
  retriesLeft = 2,
  interval = 1000
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const component = await componentImport();
      // Handle both default and non-default exports
      return 'default' in component ? component : { default: component };
    } catch (error: any) {
      if (retriesLeft > 0) {
        await new Promise((resolve) => setTimeout(resolve, interval));
        return lazyWithRetry(componentImport, retriesLeft - 1, interval * 2) as any;
      }
      
      // If we're out of retries and it's a chunk load error, force a reload
      if (error.message?.includes('Failed to fetch dynamically imported module') || 
          error.message?.includes('Loading chunk')) {
        window.location.reload();
      }
      
      throw error;
    }
  });
}
